import {Search} from 'lucide-react'
import React, {useEffect, useState} from 'react'

export interface SelectOption {
  label: string
  value: string
  icon?: string
  searchKey?: string
}

interface SelectWithSearchProps {
  options: SelectOption[]
  onSelect: (value: string) => void
  onSearchChange?: (term: string) => void
  selectedValue?: string | null
  theme?: 'light' | 'dark'
  placeholder?: string
  emptyMessage?: string
  getItemKey?: (item: SelectOption) => string
  matchValue?: (item: SelectOption, value: string) => boolean
  listMaxHeight?: number
}

const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
  options,
  onSelect,
  onSearchChange,
  selectedValue,
  theme = 'light',
  placeholder = 'Search...',
  emptyMessage = 'No items found',
  getItemKey,
  matchValue,
  listMaxHeight = 180,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItem, setSelectedItem] = useState<SelectOption | null>(null)
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(theme)

  useEffect(() => {
    const detectTheme = () => {
      const bodyTheme = document.body.getAttribute('data-bs-theme')
      const htmlTheme = document.documentElement.getAttribute('data-bs-theme')
      const detectedTheme = bodyTheme || htmlTheme || theme
      setActualTheme(detectedTheme === 'dark' ? 'dark' : 'light')
    }

    detectTheme()

    // Watch for theme changes
    const observer = new MutationObserver(detectTheme)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })

    return () => observer.disconnect()
  }, [theme])

  const isDark = actualTheme === 'dark'

  const filteredOptions = options.filter((option) => {
    const searchKey = option.searchKey || option.label
    return searchKey.toLowerCase().includes(searchTerm.toLowerCase())
  })

  useEffect(() => {
    if (selectedValue) {
      const item = options.find((opt) => {
        if (matchValue) {
          return matchValue(opt, selectedValue)
        }
        if (getItemKey) {
          return getItemKey(opt) === selectedValue
        }
        return opt.value === selectedValue || opt.label === selectedValue
      })
      if (item) {
        setSelectedItem(item)
      }
    } else {
      setSelectedItem(null)
    }
  }, [selectedValue, options, getItemKey, matchValue])

  const handleItemSelect = (item: SelectOption) => {
    setSelectedItem(item)
    onSelect(item.value)
  }

  const isItemSelected = (item: SelectOption) => {
    if (!selectedItem) return false
    if (getItemKey) {
      return getItemKey(selectedItem) === getItemKey(item)
    }
    return selectedItem.value === item.value
  }

  return (
    <div
      style={{
        backgroundColor: isDark ? '#1f2937' : '#fff',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '12px',
        marginBottom: '12px',
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          backgroundColor: isDark ? '#374151' : '#f9fafb',
          border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '12px',
        }}
      >
        <Search size={18} color={isDark ? '#9ca3af' : '#6c757d'} />
        <input
          type='text'
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            const next = e.target.value
            setSearchTerm(next)
            onSearchChange?.(next)
          }}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: 'transparent',
            color: isDark ? '#ffffff' : '#333333',
          }}
        />
      </div>

      <div
        style={{
          maxHeight: `${listMaxHeight}px`,
          overflowY: 'auto',
          border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: isDark ? '#111827' : 'transparent',
        }}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((item) => {
            const isSelected = isItemSelected(item)
            return (
              <div
                key={item.value}
                onClick={() => handleItemSelect(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#007bff' : isDark ? '#1f2937' : '#ffffff',
                  color: isSelected ? '#ffffff' : isDark ? '#e5e7eb' : '#333333',
                  transition: 'all 0.15s ease',
                  borderBottom: isDark ? '1px solid #374151' : '1px solid #f0f0f0',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f9fafb'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = isDark ? '#1f2937' : '#ffffff'
                  }
                }}
              >
                {item.icon && <span style={{fontSize: '24px'}}>{item.icon}</span>}
                <span style={{fontSize: '14px', fontWeight: isSelected ? '500' : '400'}}>
                  {item.label}
                </span>
              </div>
            )
          })
        ) : (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: isDark ? '#9ca3af' : '#6c757d',
              fontSize: '14px',
            }}
          >
            {emptyMessage}
          </div>
        )}
      </div>
    </div>
  )
}

export default SelectWithSearch
